import React, { useState, useEffect } from 'react';
import InputField from '../../commonComponent/InputField';
import { z } from 'zod';
import { getCurrentUser } from '../Authentication/auth';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

// Updated user schema to include admin_email
const userSchema = z
  .object({
    firstName: z
      .string()
      .nonempty('First name is required.')
      .max(10, 'Cannot exceed 10 characters.'),
    lastName: z.string().nonempty('Last name is required.').max(10, 'Cannot exceed 10 characters.'),
    email: z.string().email('Invalid email address.').nonempty('Email is required.'),
    password: z
      .string()
      .nonempty('Password is required.')
      .min(8, 'At least 8 characters are required.')
      .max(16, 'Password must be a maximum of 16 characters.')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character.')
      .regex(/\d/, 'Password must contain at least one number.'),
    confirmpassword: z
      .string()
      .nonempty('Confirm password is required.')
      .min(8, 'At least 8 characters are required.'),
    username: z.string().nonempty('Username is required.'),
    phone: z
      .string()
      .nonempty('Phone number is required.')
      .length(10, 'Phone number must be exactly 10 digits.')
      .regex(/^\d+$/, 'Phone number must be numeric.'),
    role: z.string().nonempty('Role is required.'),
  })
  .refine((data) => data.password === data.confirmpassword, {
    message: 'Passwords do not match.',
    path: ['confirmpassword'],
  });

const AddUser = () => {
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmpassword: '',
    username: '',
    phone: '',
    countryCode: '+91',
    role: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserDataAndRoles = async () => {
      try {
        const currentUser = await getCurrentUser();

        if (!currentUser || !currentUser.email) {
          console.error('User not found or email missing.');
          toast.error('User session expired. Please login again.');
          return;
        }

        // Store current user's email for admin_email field
        setCurrentUserEmail(currentUser.email);

        // Fetch roles
        const response = await fetch(
          `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${encodeURIComponent(currentUser.email)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentUser.access_token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setRoles(data);
        } else {
          console.error('Failed to fetch roles');
          toast.error('Failed to load roles. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching user data or roles:', error);
        toast.error('Error loading user data. Please refresh the page.');
      }
    };

    fetchUserDataAndRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const handleToggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Clear previous errors
      setErrors({});

      // Validate form data
      userSchema.parse(userData);

      const currentUser = await getCurrentUser();

      if (!currentUser || !currentUser.email) {
        toast.error('Session expired. Please login again.');
        setIsSubmitting(false);
        return;
      }

      // Prepare the request payload with admin_email
      const userPayload = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        username: userData.username,
        email: userData.email,
        country_code: userData.countryCode,
        mobile_number: parseInt(userData.phone), // Convert to number
        password: userData.password,
        role: [userData.role],
        admin_email: currentUser.email, // ADD THIS - crucial field
        firebase_id_token: null, // Optional, can be null
      };

      console.log('Sending payload:', userPayload);

      // Remove the email query parameter from URL since admin_email is now in body
      const response = await fetch(`${AQ_BASE_URL}/aq/api/createUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userPayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('User created successfully:', result);

        // Show success toast
        toast.success('User created successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Navigate back to users list after a delay
        setTimeout(() => {
          navigate('/dashboard/mdm/users');
        }, 1500);
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);

        // Show error toast with details
        const errorMessage = errorData.detail || errorData.message || 'Failed to create user.';
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setErrors({ general: errorMessage });
      }
    } catch (error) {
      if (error.errors) {
        // Zod validation errors
        const formattedErrors = {};
        error.errors.forEach(({ path, message }) => {
          formattedErrors[path[0]] = message;
        });
        setErrors(formattedErrors);

        // Show validation error toast
        toast.error('Please fix the validation errors.', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        console.error('Error creating user:', error);

        // Show general error toast
        toast.error(error.message || 'An unexpected error occurred.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setErrors({ general: error.message || 'An error occurred.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Show confirmation toast before canceling
    toast.info('Cancelled user creation.', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // Navigate back after a short delay
    setTimeout(() => {
      navigate('/dashboard/mdm/users');
    }, 500);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <NavLink
        to="/dashboard/mdm/users"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to user management</span>
      </NavLink>

      <div className="bg-white p-6 ">
        <h2 className="text-lg font-bold mb-4">Add New User</h2>

        {/* Display general error if any */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errors.general}
          </div>
        )}

        {/* Form Fields */}
        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <InputField
              id="firstName"
              name="firstName"
              label="First Name"
              type="text"
              placeholder="First Name"
              value={userData.firstName}
              onChange={handleChange}
              error={errors.firstName}
            />
          </div>
          <div className="w-1/2">
            <InputField
              id="lastName"
              name="lastName"
              label="Last Name"
              type="text"
              placeholder="Last Name"
              value={userData.lastName}
              onChange={handleChange}
              error={errors.lastName}
            />
          </div>
          <div className="w-1/2">
            <InputField
              id="username"
              name="username"
              label="User Name"
              type="text"
              placeholder="Username"
              value={userData.username}
              onChange={handleChange}
              error={errors.username}
            />
          </div>
        </div>

        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <InputField
              id="email"
              name="email"
              label="Email"
              type="email"
              placeholder="Email"
              value={userData.email}
              onChange={handleChange}
              error={errors.email}
            />
          </div>

          <div className="w-1/2 h-1/2">
            <label htmlFor="phone" className="block text-sm font-medium text-green-700 ">
              Mobile Number
            </label>
            <div className="w-full h-full flex border border-gray-300 rounded-md overflow-hidden">
              <div className="flex items-center bg-gray-50 border-r border-gray-300 px-1 py-1">
                <select
                  name="countryCode"
                  value={userData.countryCode}
                  onChange={handleChange}
                  className="text-base focus:outline-none font-normal bg-gray-50 text-gray-700"
                >
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+91">+91</option>
                </select>
              </div>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Mobile Number"
                value={userData.phone}
                onChange={handleChange}
                className="w-full py-1 focus:outline-none px-3 text-xs text-gray-700 bg-white focus:border-gray-50"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>
        </div>

        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <InputField
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={userData.password}
              onChange={handleChange}
              error={errors.password}
              showPasswordToggle
              onToggleShowPassword={handleToggleShowPassword}
            />
          </div>
          <div className="w-1/2">
            <InputField
              id="confirmpassword"
              name="confirmpassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              value={userData.confirmpassword}
              onChange={handleChange}
              error={errors.confirmpassword}
              showPasswordToggle
              onToggleShowPassword={handleToggleShowConfirmPassword}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Role</label>
          <select
            name="role"
            value={userData.role}
            onChange={handleChange}
            className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm outline-none"
          >
            <option value="" disabled>
              Select Role
            </option>
            {roles.length > 0 ? (
              roles.map((role) => (
                <option key={role.id} value={role.role_name}>
                  {role.role_name}
                </option>
              ))
            ) : (
              <option>Loading roles...</option>
            )}
          </select>
          {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role}</p>}
        </div>

        {/* Display info about who is creating this user */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            This user will be created by: <strong>{currentUserEmail || 'Loading...'}</strong>
          </p>
        </div>

        <div className="flex gap-2 mt-6 justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2 text-sm text-white rounded-md ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-black hover:text-white hover:bg-gray-800'}`}
          >
            {isSubmitting ? 'Creating...' : 'Create User'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className={`px-8 py-2 text-sm text-white rounded-md ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:text-black hover:bg-red-600'}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUser;

// import React, { useState, useEffect } from "react";
// import InputField from "../../commonComponent/InputField";
// import { z } from "zod";
// import { getCurrentUser } from "../Authentication/auth"; // correct relative path for AddUserModal

// const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;
// console.log("Environment URL:", AQ_BASE_URL); // Debug line

// const userSchema = z
//   .object({
//     firstName: z
//       .string()
//       .nonempty("First name is required.")
//       .max(10, "Cannot exceed 10 characters."),
//     lastName: z
//       .string()
//       .nonempty("Last name is required.")
//       .max(10, "Cannot exceed 10 characters."),
//     email: z
//       .string()
//       .email("Invalid email address.")
//       .nonempty("Email is required."),
//     password: z
//       .string()
//       .nonempty("Password is required.")
//       .min(8, "At least 8 characters are required.")
//       .max(16, "Password must be a maximum of 16 characters.")
//       .regex(/[A-Z]/, "Password must contain at least one uppercase letter.")
//       .regex(
//         /[!@#$%^&*(),.?":{}|<>]/,
//         "Password must contain at least one special character."
//       )
//       .regex(/\d/, "Password must contain at least one number."),
//     confirmpassword: z
//       .string()
//       .nonempty("Confirm password is required.")
//       .min(8, "At least 8 characters are required."),
//     username: z.string().nonempty("Username is required."),
//     phone: z
//       .string()
//       .nonempty("Phone number is required.")
//       .length(10, "Phone number must be exactly 10 digits.")
//       .regex(/^\d+$/, "Phone number must be numeric."),
//     role: z.string().nonempty("Role is required."),
//   })
//   .refine((data) => data.password === data.confirmpassword, {
//     message: "Passwords do not match.",
//     path: ["confirmpassword"],
//   });

// const AddUser = ({ closeModal }) => {
//   const [userData, setUserData] = useState({
//     firstName: "",
//     lastName: "",
//     email: "",
//     password: "",
//     confirmpassword: "",
//     username: "",
//     phone: "",
//     countryCode: "+91",
//     role: "",
//   });
//   const [errors, setErrors] = useState({});
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
//   const [roles, setRoles] = useState([]);

//   useEffect(() => {
//     // Fetch roles from the API when the component mounts
//     const fetchRoles = async () => {
//       try {
//         const currentUser = await getCurrentUser(); // <-- SAFE way to get email and token

//         if (!currentUser || !currentUser.email) {
//           console.error("User not found or email missing.");
//           return;
//         }

//         const response = await fetch(
//           `${AQ_BASE_URL}/aq/api/roles/getAllRoles?email=${encodeURIComponent(currentUser.email)}`,
//           {
//             method: "GET",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${currentUser.access_token}`, // optional if roles API is protected
//             },
//           }
//         );

//         if (response.ok) {
//           const data = await response.json();
//           setRoles(data);
//         } else {
//           console.error("Failed to fetch roles");
//         }
//       } catch (error) {
//         console.error("Error fetching roles:", error);
//       }
//     };

//     fetchRoles();
//   }, []); // Only run once when the component mounts

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setUserData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }));
//   };

//   const handleToggleShowPassword = () => {
//     setShowPassword(!showPassword);
//   };

//   const handleToggleShowConfirmPassword = () => {
//     setShowConfirmPassword(!showConfirmPassword);
//   };
//   const handleSubmit = async () => {
//     try {
//       userSchema.parse(userData); // validate userData first

//       const currentUser = await getCurrentUser(); // still getting current user to grab email if needed

//       if (!currentUser || !currentUser.email) {
//         console.error("User not found or email missing.");
//         return;
//       }

//       const userPayload = {
//         first_name: userData.firstName,
//         last_name: userData.lastName,
//         username: userData.username,
//         email: userData.email,
//         country_code: userData.countryCode,
//         mobile_number: userData.phone,
//         password: userData.password,
//         role: [userData.role],
//       };

//       const response = await fetch(
//         `${AQ_BASE_URL}/aq/api/createUser?email=${encodeURIComponent(currentUser.email)}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             // No Authorization header
//           },
//           body: JSON.stringify(userPayload),
//         }
//       );

//       if (response.ok) {
//         const result = await response.json();
//         console.log("User created successfully:", result);
//         window.location.reload();
//         closeModal();
//       } else {
//         const errorData = await response.json();
//         setErrors({ general: errorData.message || "Something went wrong." });
//       }
//     } catch (error) {
//       if (error.errors) {
//         const formattedErrors = {};
//         error.errors.forEach(({ path, message }) => {
//           formattedErrors[path[0]] = message;
//         });
//         setErrors(formattedErrors);
//       } else {
//         console.error("Error creating user:", error);
//       }
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
//       <div className="bg-white p-6 rounded-lg w-2/4">
//         <h2 className="text-lg font-bold mb-4">Add New User</h2>

//         {/* Form Fields */}
//         <div className="flex gap-4 mb-4">
//           <div className="w-1/2">
//             <InputField
//               id="firstName"
//               name="firstName"
//               label="First Name"
//               type="text"
//               placeholder="First Name"
//               value={userData.firstName}
//               onChange={handleChange}
//               error={errors.firstName}
//             />
//           </div>
//           <div className="w-1/2">
//             <InputField
//               id="lastName"
//               name="lastName"
//               label="Last Name"
//               type="text"
//               placeholder="Last Name"
//               value={userData.lastName}
//               onChange={handleChange}
//               error={errors.lastName}
//             />
//           </div>
//           <div className="w-1/2">
//             <InputField
//               id="username"
//               name="username"
//               label="User Name"
//               type="text"
//               placeholder="Username"
//               value={userData.username}
//               onChange={handleChange}
//               error={errors.username}
//             />
//           </div>
//         </div>

//         <div className="flex gap-4 mb-4">
//           <div className="w-1/2">
//             <InputField
//               id="email"
//               name="email"
//               label="Email"
//               type="email"
//               placeholder="Email"
//               value={userData.email}
//               onChange={handleChange}
//               error={errors.email}
//             />
//           </div>

//           <div className="w-1/2 h-1/2">
//             <label
//               htmlFor="phone"
//               className="block text-sm font-medium text-green-700 "
//             >
//               Mobile Number
//             </label>
//             <div className="w-full h-full flex border border-gray-300 rounded-md overflow-hidden">
//               <div className="flex items-center bg-gray-50 border-r border-gray-300 px-1 py-1">
//                 <select
//                   name="countryCode"
//                   value={userData.countryCode}
//                   onChange={handleChange}
//                   className="text-base focus:outline-none font-normal bg-gray-50 text-gray-700"
//                 >
//                   <option value="+1">+1</option>
//                   <option value="+44">+44</option>
//                   <option value="+91">+91</option>
//                   {/* Add more country codes as needed */}
//                 </select>
//               </div>
//               <input
//                 id="phone"
//                 name="phone"
//                 type="tel"
//                 placeholder="Mobile Number"
//                 value={userData.phone}
//                 onChange={handleChange}
//                 className="w-full py-1 focus:outline-none px-3 text-xs text-gray-700 bg-white focus:border-gray-50"
//               />
//             </div>
//             {errors.phone && (
//               <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
//             )}
//           </div>
//         </div>

//         <div className="flex gap-4 mb-4">
//           <div className="w-1/2">
//             <InputField
//               id="password"
//               name="password"
//               label="Password"
//               type={showPassword ? "text" : "password"}
//               placeholder="Password"
//               value={userData.password}
//               onChange={handleChange}
//               error={errors.password}
//               showPasswordToggle
//               onToggleShowPassword={handleToggleShowPassword}
//             />
//           </div>
//           <div className="w-1/2">
//             <InputField
//               id="confirmpassword"
//               name="confirmpassword"
//               label="Confirm Password"
//               type={showConfirmPassword ? "text" : "password"}
//               placeholder="Confirm Password"
//               value={userData.confirmpassword}
//               onChange={handleChange}
//               error={errors.confirmpassword}
//               showPasswordToggle
//               onToggleShowPassword={handleToggleShowConfirmPassword}
//             />
//           </div>
//         </div>

//         <div className="mb-4">
//           <label className="block text-sm font-medium text-gray-700">
//             Role
//           </label>
//           <select
//             name="role"
//             value={userData.role}
//             onChange={handleChange}
//             className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm outline-none"
//           >
//             <option value="" disabled>Select Role</option>
//             {roles.length > 0 ? (
//               roles.map((role) => (
//                 <option key={role.id} value={role.role_name}>
//                   {role.role_name}
//                 </option>
//               ))
//             ) : (
//               <option>Loading...</option>
//             )}
//           </select>
//           {errors.role && (
//             <p className="text-red-500 text-sm mt-1">{errors.role}</p>
//           )}
//         </div>

//         <div className="flex gap-2 mt-6 justify-end">
//           <button
//             type="button"
//             onClick={handleSubmit}
//             className="px-6 py-2 text-sm text-white bg-cyan-500 hover:text-black rounded-md"
//           >
//             Create User
//           </button>
//           <button
//             type="button"
//             onClick={closeModal}
//             className="px-8 py-2 text-sm text-black hover:text-white bg-red-500 rounded-md"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddUser;
